import React, { useEffect, useRef, useCallback } from 'react';

const cropToSignature = (canvas) => {
  const ctx = canvas.getContext('2d');
  const w = canvas.width, h = canvas.height;
  const px = ctx.getImageData(0, 0, w, h).data;
  let x0 = w, y0 = h, x1 = 0, y1 = 0;
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      const i = (y * w + x) * 4;
      if (px[i + 3] > 10 && px[i] < 230) {
        if (x < x0) x0 = x; if (x > x1) x1 = x;
        if (y < y0) y0 = y; if (y > y1) y1 = y;
      }
    }
  }
  if (x1 <= x0) return canvas.toDataURL('image/png');
  const pad = Math.round(Math.min(w, h) * 0.06);
  x0 = Math.max(0, x0 - pad); y0 = Math.max(0, y0 - pad);
  x1 = Math.min(w, x1 + pad); y1 = Math.min(h, y1 + pad);
  const out = document.createElement('canvas');
  out.width = x1 - x0; out.height = y1 - y0;
  const octx = out.getContext('2d');
  octx.fillStyle = '#ffffff';
  octx.fillRect(0, 0, out.width, out.height);
  octx.drawImage(canvas, x0, y0, out.width, out.height, 0, 0, out.width, out.height);
  return out.toDataURL('image/png');
};

export default function FirmaCanvasModal({
  open,
  onClose,
  onConfirm,
  titulo = 'Firma',
  loading = false,
  nombreLabel,
  nombreValue,
  onNombreChange,
}) {
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const lastPos = useRef(null);

  useEffect(() => {
    if (open) document.body.style.overflow = 'hidden';
    else document.body.style.overflow = '';
    return () => { document.body.style.overflow = ''; };
  }, [open]);

  useEffect(() => {
    if (!open || !canvasRef.current) return;
    drawing.current = false;
    lastPos.current = null;
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    canvas.width = rect.width * window.devicePixelRatio;
    canvas.height = rect.height * window.devicePixelRatio;
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.scale(window.devicePixelRatio, window.devicePixelRatio);
    ctx.strokeStyle = '#1a1a2e';
    ctx.lineWidth = 2.5;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
  }, [open]);

  const getPos = (e, canvas) => {
    const rect = canvas.getBoundingClientRect();
    const src = e.touches ? e.touches[0] : e;
    return { x: src.clientX - rect.left, y: src.clientY - rect.top };
  };

  const startDraw = useCallback((e) => {
    e.preventDefault();
    drawing.current = true;
    lastPos.current = getPos(e, canvasRef.current);
  }, []);

  const draw = useCallback((e) => {
    if (!drawing.current) return;
    e.preventDefault();
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const pos = getPos(e, canvas);
    ctx.beginPath();
    ctx.moveTo(lastPos.current.x, lastPos.current.y);
    ctx.lineTo(pos.x, pos.y);
    ctx.stroke();
    lastPos.current = pos;
  }, []);

  const stopDraw = useCallback((e) => {
    if (e) e.preventDefault();
    drawing.current = false;
  }, []);

  const limpiar = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width / window.devicePixelRatio, canvas.height / window.devicePixelRatio);
  };

  const guardar = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const data = ctx.getImageData(0, 0, canvas.width, canvas.height);
    const hasSignature = data.data.some((v, i) => i % 4 === 3 && v > 0);
    if (!hasSignature) { alert('Por favor dibuje su firma antes de guardar.'); return; }
    onConfirm(cropToSignature(canvas));
  };

  if (!open) return null;

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 9999,
      background: '#fff', display: 'flex', flexDirection: 'column',
    }}>
      {/* Header */}
      <div style={{
        background: '#1c2d5a', color: '#fff', padding: '14px 20px',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        flexShrink: 0,
      }}>
        <div style={{ fontWeight: 700, fontSize: 16 }}>{titulo}</div>
        <button
          onClick={onClose}
          style={{ background: 'rgba(255,255,255,0.2)', border: 'none', color: '#fff', borderRadius: 8, padding: '8px 14px', fontSize: 16, cursor: 'pointer', fontWeight: 700 }}
        >
          ✕
        </button>
      </div>

      {/* Instruction */}
      <div style={{ textAlign: 'center', padding: '10px 16px', fontSize: 13, color: '#7f8c8d', flexShrink: 0 }}>
        Use el dedo o el mouse para firmar dentro del recuadro
      </div>

      {/* Canvas area — tamaño fijo centrado, consistente en desktop y mobile */}
      <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0 16px', minHeight: 0 }}>
        <div style={{ position: 'relative', width: '100%', maxWidth: 540 }}>
          <canvas
            ref={canvasRef}
            style={{
              display: 'block',
              width: '100%',
              height: 220,
              border: '2px solid #dee2e6',
              borderRadius: 12,
              background: '#fff',
              cursor: 'crosshair',
              touchAction: 'none',
            }}
            onMouseDown={startDraw}
            onMouseMove={draw}
            onMouseUp={stopDraw}
            onMouseLeave={stopDraw}
            onTouchStart={startDraw}
            onTouchMove={draw}
            onTouchEnd={stopDraw}
          />
          <div style={{
            position: 'absolute', bottom: 56, left: '8%', right: '8%',
            height: 1, background: '#dee2e6', pointerEvents: 'none',
          }} />
        </div>
      </div>

      {/* Optional nombre input */}
      {onNombreChange && (
        <div style={{ padding: '12px 16px 0', flexShrink: 0 }}>
          <label style={{ fontSize: 12, color: '#7f8c8d', fontWeight: 600, display: 'block', marginBottom: 4 }}>
            {nombreLabel || 'Nombre del firmante'}
          </label>
          <input
            type="text"
            value={nombreValue || ''}
            onChange={e => onNombreChange(e.target.value)}
            placeholder="Ingrese nombre completo"
            style={{
              width: '100%', padding: '11px 14px', borderRadius: 8, border: '1px solid #ced4da',
              fontSize: 15, boxSizing: 'border-box',
            }}
          />
        </div>
      )}

      {/* Footer buttons */}
      <div style={{ padding: '16px', display: 'flex', gap: 10, flexShrink: 0 }}>
        <button
          onClick={onClose}
          disabled={loading}
          style={{ flex: 1, padding: '13px', borderRadius: 8, border: '1px solid #dee2e6', background: '#f8f9fa', fontWeight: 600, fontSize: 14, cursor: loading ? 'default' : 'pointer' }}
        >
          Cancelar
        </button>
        <button
          onClick={limpiar}
          disabled={loading}
          style={{ flex: 1, padding: '13px', borderRadius: 8, border: '1px solid #dee2e6', background: '#f8f9fa', fontWeight: 600, fontSize: 14, cursor: loading ? 'default' : 'pointer' }}
        >
          Limpiar
        </button>
        <button
          onClick={guardar}
          disabled={loading}
          style={{ flex: 2, padding: '13px', borderRadius: 8, border: 'none', background: loading ? '#bdc3c7' : '#1c2d5a', color: '#fff', fontWeight: 700, fontSize: 15, cursor: loading ? 'default' : 'pointer' }}
        >
          {loading ? 'Guardando...' : 'Guardar firma'}
        </button>
      </div>
    </div>
  );
}
